import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';

interface DashboardPreferencesState {
  hidden: string[];
  expanded: string[];
  customOrder: string[];
  density: 'comfortable' | 'compact';
}

interface DashboardPreferencesContext extends DashboardPreferencesState {
  toggleExpanded: (id: string) => void;
  hideWidget: (id: string) => void;
  showWidget: (id: string) => void;
  reorder: (orderedIds: string[]) => void;
  setDensity: (d: 'comfortable' | 'compact') => void;
}

const DEFAULT_PREFS: DashboardPreferencesState = {
  hidden: [],
  expanded: [],
  customOrder: [],
  density: 'comfortable'
};

const STORAGE_KEY = 'dashboard:prefs:v1';

const DashboardPreferencesCtx = createContext<DashboardPreferencesContext | null>(null);

export const DashboardPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [prefs, setPrefs] = useState<DashboardPreferencesState>(DEFAULT_PREFS);

  // Load once
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setPrefs({ ...DEFAULT_PREFS, ...parsed });
      }
    } catch (err) {
      // ignore
    }
  }, []);

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (err) {
      // ignore
    }
  }, [prefs]);

  const toggleExpanded = useCallback((id: string) => {
    setPrefs(p => ({
      ...p,
      expanded: p.expanded.includes(id)
        ? p.expanded.filter(x => x !== id)
        : [...p.expanded, id]
    }));
  }, []);

  const hideWidget = useCallback((id: string) => {
    setPrefs(p => ({ ...p, hidden: Array.from(new Set([...p.hidden, id])) }));
  }, []);

  const showWidget = useCallback((id: string) => {
    setPrefs(p => ({ ...p, hidden: p.hidden.filter(x => x !== id) }));
  }, []);

  const reorder = useCallback((orderedIds: string[]) => {
    setPrefs(p => ({ ...p, customOrder: orderedIds }));
  }, []);

  const setDensity = useCallback((d: 'comfortable' | 'compact') => {
    setPrefs(p => ({ ...p, density: d }));
  }, []);

  return (
    <DashboardPreferencesCtx.Provider value={{ ...prefs, toggleExpanded, hideWidget, showWidget, reorder, setDensity }}>
      {children}
    </DashboardPreferencesCtx.Provider>
  );
};

export const useDashboardPreferences = () => {
  const ctx = useContext(DashboardPreferencesCtx);
  if (!ctx) {
    // Fallback: non-provider usage (should wrap higher in tree later)
    return {
      ...DEFAULT_PREFS,
      toggleExpanded: () => undefined,
      hideWidget: () => undefined,
      showWidget: () => undefined,
      reorder: () => undefined,
      setDensity: () => undefined
    } as DashboardPreferencesContext;
  }
  return ctx;
};